import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  TextField,
  IconButton,
  Badge,
  Tabs,
  Tab,
  InputAdornment,
  Container,
} from "@mui/material";

import { ShoppingCart, FavoriteBorder, Search } from "@mui/icons-material";

import { jwtDecode } from "jwt-decode"; // ✅ FIXED

import { useNavigate, Outlet } from "react-router-dom";
import CartModal from "./CartModal";
import ShopUserMenu from "./ShopUserMenu";
import axios from "axios";
import configLocal from "../../config/config.local.json";
import { useCart } from "../../context/CartContext.js";
const PRIMARY_COLOR = "#2E4053";
const ACCENT_COLOR = "#D35400";

const ShopNavbar = () => {
  const navigate = useNavigate();
  const BASE_URL = configLocal.baseURL;

  const [categories, setCategories] = useState([]);

  const [cartOpen, setCartOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState(null);
  // const [cartItems, setCartItems] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);

  const { totalItems } = useCart();

  // const { cartItems, totalItems } = useCart();
  const token = localStorage.getItem("token");

  // ✅ Decode token safely
  const currentUser = useMemo(() => {
    if (!token) return null;

    try {
      const decoded = jwtDecode(token);

      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("token");
        return null;
      }

      return decoded;
    } catch (err) {
      console.error("Invalid token:", err);
      localStorage.removeItem("token");
      return null;
    }
  }, [token]);

  // ✅ Fetch Company
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const isLocalhost = window.location.hostname.includes("localhost");

        const shopParam = isLocalhost ? "test.spadeplatform.com" : undefined;

        const url = shopParam
          ? `${BASE_URL}/api/public/shop/company?shop=${shopParam}`
          : `${BASE_URL}/api/public/shop/company`;

        const res = await axios.get(url);
        if (res?.status === 200) {
          setCompany(res.data.company);
        } else {
          console.error("Failed to fetch company:", res);
        }
      } catch (err) {
        console.error("Failed to fetch company:", err);
      }
    };

    fetchCompany();
  }, [BASE_URL]);
  // ✅ LOAD cart on page load

  // ✅ Fetch Wishlist Count
  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/public/wishlist?shop=test.spadeplatform.com`
        );
        setWishlistCount(res.data?.data?.length || 0);
      } catch (err) {
        console.error("Wishlist fetch error:", err);
      }
    };

    fetchWishlist();
  }, [BASE_URL]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && search.trim()) {
      navigate(`/shop/products?search=${encodeURIComponent(search.trim())}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleRemove = (id) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
    setCartCount((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleCheckout = () => {
    navigate("/shop/checkout");
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        sx={{ backgroundColor: PRIMARY_COLOR, zIndex: 1201 }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ gap: { xs: 1, md: 4 } }}>
            {/* Logo */}
            {company?.logo ? (
              <Box
                component="img"
                src={company.logo}
                alt={company.companyName || "Company Logo"}
                onClick={() => navigate("/shop")}
                onError={(e) => (e.target.style.display = "none")}
                sx={{
                  height: 52,
                  cursor: "pointer",
                  display: { xs: "none", sm: "block" },
                  objectFit: "contain",
                }}
              />
            ) : (
              <Typography
                variant="h5"
                noWrap
                onClick={() => navigate("/")}
                sx={{
                  fontWeight: 800,
                  cursor: "pointer",
                  display: { xs: "none", sm: "block" },
                }}
              >
                {company?.companyName || "MY"}
                <span style={{ color: ACCENT_COLOR }}>SHOP</span>
              </Typography>
            )}

            {/* Search */}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                sx={{
                  maxWidth: "700px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    borderRadius: "50px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Icons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: 2 },
              }}
            >
              <IconButton
                color="inherit"
                onClick={() => navigate("/shop/wishlist")}
              >
                <Badge badgeContent={wishlistCount} color="error">
                  <FavoriteBorder />
                </Badge>
              </IconButton>

              <IconButton color="inherit" onClick={() => setCartOpen(true)}>
                <Badge
                  badgeContent={totalItems}
                  color="error"
                  overlap="circular"
                >
                  <ShoppingCart />
                </Badge>
              </IconButton>

              <ShopUserMenu user={currentUser} handleLogout={handleLogout} />
            </Box>
          </Toolbar>
        </Container>

        {/* Categories */}
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.05)",
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Container maxWidth="xl">
            <Tabs
              value={false}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{
                style: {
                  backgroundColor: ACCENT_COLOR,
                },
              }}
            >
              {categories.map((cat) => (
                <Tab
                  key={cat.id}
                  label={cat.name}
                  onClick={() => navigate(`/category/${cat.slug}`)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    minWidth: 120,
                  }}
                />
              ))}
            </Tabs>
          </Container>
        </Box>
      </AppBar>

      <CartModal
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        onCheckout={handleCheckout}
      />

      <Box
        sx={{
          mt: { xs: "120px", sm: "140px" },
          p: 3,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default ShopNavbar;
